package org.catharsis.spendtel.feature.presentation.financialgoal

import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.spring
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.scale
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.zIndex
import org.catharsis.spendtel.feature.data.FinancialGoal
import org.catharsis.spendtel.feature.presentation.expensereport.accentForIndex

private data class GoalBubblePlacement(
    val goal: FinancialGoal,
    val x: Float,
    val y: Float,
    val diameter: Dp,
    val zIndex: Float = 1f,
)

private val mobileGoalPlacements = listOf(
    GoalBubblePlacement(FinancialGoal.SaveMore, 0.22f, 0.14f, 118.dp, 3f),
    GoalBubblePlacement(FinancialGoal.PayOffDebt, 0.76f, 0.12f, 96.dp, 2f),
    GoalBubblePlacement(FinancialGoal.EmergencyFund, 0.50f, 0.28f, 112.dp, 4f),
    GoalBubblePlacement(FinancialGoal.ReduceSpending, 0.16f, 0.40f, 90.dp, 1f),
    GoalBubblePlacement(FinancialGoal.UnderstandHabits, 0.82f, 0.36f, 104.dp, 2f),
    GoalBubblePlacement(FinancialGoal.BigPurchase, 0.34f, 0.58f, 116.dp, 3f),
    GoalBubblePlacement(FinancialGoal.InvestMore, 0.70f, 0.60f, 94.dp, 1f),
    GoalBubblePlacement(FinancialGoal.StickToBudget, 0.50f, 0.78f, 108.dp, 2f),
)

@Composable
fun FinancialGoalBubbleCloud(
    selectedGoal: FinancialGoal?,
    onGoalSelected: (FinancialGoal) -> Unit,
    modifier: Modifier = Modifier,
) {
    BoxWithConstraints(
        modifier = modifier
            .fillMaxWidth()
            .height(430.dp),
    ) {
        val containerWidth = maxWidth
        val containerHeight = maxHeight

        mobileGoalPlacements.forEachIndexed { index, placement ->
            val selected = selectedGoal == placement.goal
            val accentColor = accentForIndex(index)
            val scale by animateFloatAsState(
                targetValue = if (selected) 1.08f else 1f,
                animationSpec = spring(stiffness = Spring.StiffnessMediumLow),
                label = "goalBubbleScale",
            )
            val bubbleSize = placement.diameter
            val xOffset = (containerWidth * placement.x) - (bubbleSize / 2)
            val yOffset = (containerHeight * placement.y) - (bubbleSize / 2)

            FinancialGoalCloudBubble(
                goal = placement.goal,
                accentColor = accentColor,
                selected = selected,
                onClick = { onGoalSelected(placement.goal) },
                modifier = Modifier
                    .zIndex(if (selected) 20f else placement.zIndex)
                    .offset(x = xOffset, y = yOffset)
                    .size(bubbleSize)
                    .scale(scale),
            )
        }
    }
}

@Composable
private fun FinancialGoalCloudBubble(
    goal: FinancialGoal,
    accentColor: Color,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Box(
        modifier = modifier
            .shadow(
                elevation = if (selected) 14.dp else 6.dp,
                shape = CircleShape,
            )
            .clip(CircleShape)
            .background(accentColor)
            .then(
                if (selected) {
                    Modifier.border(3.dp, Color.White, CircleShape)
                } else {
                    Modifier
                },
            )
            .clickable(onClick = onClick),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier.padding(horizontal = 10.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Icon(
                imageVector = goal.icon(),
                contentDescription = null,
                tint = Color.White,
                modifier = Modifier.size(22.dp),
            )
            Text(
                text = goal.cloudLabel,
                modifier = Modifier.padding(top = 6.dp),
                style = MaterialTheme.typography.labelMedium,
                fontWeight = FontWeight.Bold,
                color = Color.White,
                textAlign = TextAlign.Center,
                lineHeight = 14.sp,
            )
        }
    }
}
